import React, { useMemo, useState } from 'react';
import { View, Text, TouchableOpacity, Pressable, Image, ImageBackground, Platform, StyleSheet } from 'react-native';
import {
  CHALKBOARD_ASPECT, CHALKBOARD_FRAME_SLICE, CHALKBOARD_COLORS, CHALK_ASPECT,
  chalkboardLayout, reportCardLabel,
} from '../../utils/chalkboardLayout';
import { CANVA_TITLE_FONT } from '../../utils/hamburgerMenu';

const BOARD_IMG = require('../../../assets/icons/chalkboard.webp');
const CHALK_IMG = require('../../../assets/icons/chalk-stick.webp');

const isWeb = Platform.OS === 'web';
// Report-count label and "Add grade" match the search bar input font (homeStyles.searchInput).
const SEARCH_INPUT_FONT = 'VastagoGrotesk';
// Lets taps on the label and grade fall through to the full-board press target.
const PASS_THROUGH = { pointerEvents: 'none' };

const assetUri = (mod) => {
  if (typeof mod === 'string') return mod;
  return Image.resolveAssetSource?.(mod)?.uri || mod?.uri || mod?.default || '';
};

const prefersReducedMotion = () => {
  try {
    return isWeb && typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/**
 * Venue report card on a framed chalkboard (Canva page 13): count in peach,
 * grade in chalk, and a chalk stick on the bottom rail that opens Submit a report.
 * The whole board is a target too: with reports it opens them (what makes the
 * grade); with none it opens Submit a report, so nobody has to find the chalk.
 * The board fills the size its parent gives it (`style`); without one it keeps the
 * Canva aspect ratio.
 */
export default function ReportCardChalkboard({ grade, reportCount, onOpenReports, onSubmitReport, style }) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [chalkHover, setChalkHover] = useState(false);
  const [boardHover, setBoardHover] = useState(false);
  const reduceMotion = useMemo(prefersReducedMotion, []);
  const L = chalkboardLayout(size.width, size.height);
  const hasReports = reportCount > 0;
  const onBoardPress = hasReports ? onOpenReports : onSubmitReport;
  // On an empty board the chalk lifts while the board is hovered, hinting at what a click does.
  const chalkRaised = chalkHover || (boardHover && !hasReports);

  const onLayout = (e) => {
    const { width, height } = e.nativeEvent.layout;
    if (Math.abs(width - size.width) > 0.5 || Math.abs(height - size.height) > 0.5) setSize({ width, height });
  };

  const content = size.width > 0 && (
    <>
      <Pressable
        testID="chalkboard-board-press"
        onPress={() => onBoardPress?.()}
        onHoverIn={() => setBoardHover(true)}
        onHoverOut={() => setBoardHover(false)}
        accessibilityRole="button"
        accessibilityLabel={hasReports
          ? `Grade ${grade}, ${reportCardLabel(reportCount)}. Open report cards`
          : 'No report cards yet. Submit a report'}
        style={[StyleSheet.absoluteFill, isWeb ? { cursor: 'pointer' } : null]}
      />

      {/* Decorative: the board button's label already reads the count and grade. */}
      <View accessibilityElementsHidden importantForAccessibility="no-hide-descendants" aria-hidden style={[StyleSheet.absoluteFill, PASS_THROUGH]}>
        <Text
          style={{
            position: 'absolute', left: 0, right: 0, top: L.titleTop, textAlign: 'center',
            fontFamily: 'VastagoGrotesk', fontSize: L.titleSize, lineHeight: Math.round(L.titleSize * 1.4),
            color: CHALKBOARD_COLORS.title,
          }}
        >
          {reportCardLabel(reportCount)}
        </Text>
      </View>

      <View
        accessibilityElementsHidden importantForAccessibility="no-hide-descendants" aria-hidden
        style={{
          ...PASS_THROUGH,
          position: 'absolute', left: 0, right: 0, alignItems: 'center',
          top: L.gradeCenterY - Math.round(L.gradeSize * 0.6),
        }}
      >
        <Text
          style={{
            fontFamily: CANVA_TITLE_FONT, fontSize: L.gradeSize, lineHeight: Math.round(L.gradeSize * 1.2),
            color: CHALKBOARD_COLORS.grade, textAlign: 'center',
            // A soft halo so the letter reads as chalk dust rather than a flat fill;
            // it brightens on hover when a click will open the report cards.
            ...(isWeb ? {
              textShadow: boardHover && hasReports ? '0 0 6px rgba(250,247,224,0.7)' : '0 0 2px rgba(250,247,224,0.45)',
              transition: 'text-shadow 0.2s ease-out',
            } : {}),
          }}
        >
          {grade}
        </Text>
      </View>

      <TouchableOpacity
        activeOpacity={0.85}
        onPress={onSubmitReport}
        onMouseEnter={() => setChalkHover(true)}
        onMouseLeave={() => setChalkHover(false)}
        accessibilityRole="button"
        accessibilityLabel="Submit a report"
        hitSlop={{ top: 10, bottom: 10, left: 8, right: 8 }}
        style={{
          position: 'absolute', left: L.chalkLeft, top: L.chalkTop,
          width: L.chalkWidth, height: L.chalkHeight,
          ...(isWeb ? {
            cursor: 'pointer',
            transform: chalkRaised && !reduceMotion ? 'translateY(-3px) rotate(-6deg)' : 'none',
            transition: reduceMotion ? 'none' : 'transform 0.2s ease-out',
          } : {}),
        }}
      >
        <Image
          source={CHALK_IMG}
          resizeMode="contain"
          style={{
            width: L.chalkWidth, height: L.chalkHeight, aspectRatio: CHALK_ASPECT,
            ...(isWeb ? { filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.35))' } : {}),
          }}
        />
      </TouchableOpacity>

      {/* Lauren 2026-10-21: the words "Add grade" next to the chalk, on the bottom rail. */}
      <TouchableOpacity
        testID="chalkboard-add-grade"
        onPress={onSubmitReport}
        onMouseEnter={() => setChalkHover(true)}
        onMouseLeave={() => setChalkHover(false)}
        accessibilityRole="button"
        accessibilityLabel="Add grade"
        style={{
          position: 'absolute', right: Math.max(0, size.width - L.chalkLeft + 6), top: L.chalkTop,
          height: L.chalkHeight, justifyContent: 'center',
          ...(isWeb ? { cursor: 'pointer' } : {}),
        }}
      >
        <Text style={{ fontFamily: 'VastagoGrotesk', fontWeight: '700', fontSize: Math.max(11, Math.round(L.titleSize * 0.7)), color: CHALKBOARD_COLORS.title }}>
          Add grade
        </Text>
      </TouchableOpacity>
    </>
  );

  const boxStyle = [{ width: '100%', aspectRatio: CHALKBOARD_ASPECT, position: 'relative' }, style];

  if (isWeb) {
    // 9-slice the frame so the wood keeps its thickness however the column stretches.
    const [t, r, b, l] = CHALKBOARD_FRAME_SLICE;
    return (
      <View style={boxStyle} onLayout={onLayout}>
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0, boxSizing: 'border-box',
            borderStyle: 'solid', borderColor: 'transparent',
            borderWidth: `${L.frame}px`,
            borderImageSource: `url(${assetUri(BOARD_IMG)})`,
            borderImageSlice: `${t} ${r} ${b} ${l} fill`,
            borderImageRepeat: 'stretch',
            filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.25))',
          }}
        />
        {content}
      </View>
    );
  }

  return (
    <ImageBackground source={BOARD_IMG} resizeMode="stretch" style={boxStyle} onLayout={onLayout}>
      {content}
    </ImageBackground>
  );
}
